import React, { useState, useEffect } from 'react';
import { 
  Users, Briefcase, CheckCircle, Send, MapPin, Clock,
  Star, Heart, Sun, Zap, Award, TrendingUp, Phone,
  Mail, ArrowRight, Building2
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const TrabajaPage = () => {
  const [selectedPosition, setSelectedPosition] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const positions = [
    {
      id: 1,
      title: 'Técnico Instalador Solar',
      department: 'Operaciones',
      location: 'Asunción y Gran Asunción',
      type: 'Tiempo completo',
      description: 'Buscamos técnicos eléctricos o electromecánicos para instalación de sistemas solares residenciales y comerciales.',
      requirements: [
        'Formación técnica en electricidad o electromecánica',
        'Experiencia mínima de 1 año en instalaciones eléctricas',
        'Licencia de conducir vigente',
        'Disponibilidad para viajar ocasionalmente',
        'Trabajo en altura (deseable)'
      ],
      benefits: [
        'Salario competitivo + comisiones',
        'Capacitación continua',
        'Herramientas de trabajo',
        'Seguro médico',
        'Oportunidades de crecimiento'
      ]
    },
    {
      id: 2,
      title: 'Ejecutivo Comercial',
      department: 'Ventas',
      location: 'Asunción',
      type: 'Tiempo completo',
      description: 'Responsable de la gestión comercial de clientes residenciales y comerciales interesados en energía solar.',
      requirements: [
        'Experiencia en ventas consultivas',
        'Excelentes habilidades de comunicación',
        'Orientación a resultados',
        'Conocimientos básicos de energía (deseable)',
        'Vehículo propio (deseable)'
      ],
      benefits: [
        'Salario base + comisiones atractivas',
        'Capacitación en productos',
        'Ambiente dinámico',
        'Plan de carrera',
        'Bonos por cumplimiento'
      ]
    },
    {
      id: 3,
      title: 'Ingeniero de Proyectos',
      department: 'Ingeniería',
      location: 'Asunción',
      type: 'Tiempo completo',
      description: 'Diseño y dimensionamiento de sistemas solares, supervisión técnica de proyectos y soporte al equipo comercial.',
      requirements: [
        'Ingeniería Eléctrica o Electromecánica',
        'Experiencia en diseño de sistemas solares (deseable)',
        'Manejo de AutoCAD y herramientas de diseño',
        'Conocimiento de normativas eléctricas',
        'Inglés técnico'
      ],
      benefits: [
        'Salario competitivo',
        'Proyectos desafiantes',
        'Capacitación internacional',
        'Trabajo híbrido',
        'Seguro médico premium'
      ]
    }
  ];

  const culture = [
    {
      icon: Sun,
      title: 'Propósito',
      description: 'Trabajamos por un futuro sostenible. Cada día contribuimos a un Paraguay más limpio.'
    },
    {
      icon: Users,
      title: 'Equipo',
      description: 'Somos un equipo diverso y colaborativo donde cada voz cuenta.'
    },
    {
      icon: TrendingUp,
      title: 'Crecimiento',
      description: 'Invertimos en el desarrollo profesional de nuestro equipo con capacitaciones constantes.'
    },
    {
      icon: Heart,
      title: 'Bienestar',
      description: 'El balance vida-trabajo es importante. Promovemos ambientes flexibles y saludables.'
    }
  ];

  const perks = [
    'Salario competitivo',
    'Capacitación continua',
    'Seguro médico',
    'Ambiente colaborativo',
    'Oportunidades de crecimiento',
    'Trabajo con propósito',
    'Equipos modernos',
    'Eventos de integración'
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="trabaja-page">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-zinc-900 via-emerald-900 to-zinc-900 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-20 w-72 h-72 bg-emerald-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-emerald-400/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex items-center gap-2 text-emerald-400 mb-6">
            <Briefcase className="w-5 h-5" />
            <span className="text-sm font-medium tracking-wider uppercase">Únete al Equipo</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Trabaja con Nosotros
            <span className="block text-emerald-400">Construí el Futuro de la Energía</span>
          </h1>
          
          <p className="text-xl text-zinc-300 max-w-2xl mb-8">
            Buscamos personas apasionadas por la sostenibilidad que quieran 
            ser parte de la revolución energética en Paraguay.
          </p>

          <a 
            href="#vacantes"
            className="inline-flex items-center gap-2 px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl transition-all"
          >
            Ver Vacantes Disponibles
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>

      {/* Culture */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Nuestra Cultura
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              Lo que nos hace únicos como equipo
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {culture.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-zinc-900 mb-2">{item.title}</h3>
                  <p className="text-zinc-600">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Perks */}
      <section className="py-16 bg-emerald-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white text-center mb-8">
            Beneficios de ser parte de Zenex
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            {perks.map((perk, index) => (
              <div 
                key={index}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-white"
              >
                <CheckCircle className="w-4 h-4 text-emerald-300" />
                {perk}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section id="vacantes" className="py-20 bg-zinc-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Vacantes Disponibles
            </h2>
            <p className="text-lg text-zinc-600">
              Encontrá tu próximo desafío profesional
            </p>
          </div>

          <div className="space-y-6">
            {positions.map((position) => (
              <div 
                key={position.id}
                className="bg-white rounded-xl shadow-sm border border-zinc-100 overflow-hidden"
              >
                <div 
                  className="p-6 cursor-pointer hover:bg-zinc-50 transition-colors"
                  onClick={() => setSelectedPosition(selectedPosition === position.id ? null : position.id)}
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <h3 className="text-xl font-bold text-zinc-900">{position.title}</h3>
                      <div className="flex flex-wrap gap-4 mt-2 text-sm text-zinc-500">
                        <span className="flex items-center gap-1">
                          <Building2 className="w-4 h-4" />
                          {position.department}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {position.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {position.type}
                        </span>
                      </div>
                    </div>
                    <button className="px-4 py-2 bg-emerald-100 text-emerald-600 rounded-lg font-medium hover:bg-emerald-200 transition-colors">
                      {selectedPosition === position.id ? 'Ver menos' : 'Ver más'}
                    </button>
                  </div>
                </div>

                {selectedPosition === position.id && (
                  <div className="p-6 border-t border-zinc-100 bg-zinc-50">
                    <p className="text-zinc-600 mb-6">{position.description}</p>
                    
                    <div className="grid md:grid-cols-2 gap-8">
                      <div>
                        <h4 className="font-bold text-zinc-900 mb-3">Requisitos:</h4>
                        <ul className="space-y-2">
                          {position.requirements.map((req, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-zinc-600">
                              <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                              {req}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-bold text-zinc-900 mb-3">Ofrecemos:</h4>
                        <ul className="space-y-2">
                          {position.benefits.map((benefit, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-zinc-600">
                              <Star className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                              {benefit}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="mt-6 pt-6 border-t border-zinc-200">
                      <a 
                        href={`mailto:zenexparaguay@gmail.com?subject=Postulación: ${position.title}`}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
                      >
                        <Send className="w-5 h-5" />
                        Postularme
                      </a>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* No Position Found */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-zinc-900 mb-4">
            ¿No encontrás lo que buscás?
          </h2>
          <p className="text-zinc-600 mb-8">
            Siempre estamos abiertos a conocer talento. Envianos tu CV y te contactaremos 
            cuando surja una oportunidad acorde a tu perfil.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="mailto:zenexparaguay@gmail.com?subject=CV Espontáneo"
              className="flex items-center gap-2 px-6 py-3 bg-zinc-800 text-white font-semibold rounded-lg hover:bg-zinc-900 transition-colors"
            >
              <Mail className="w-5 h-5" />
              Enviar CV Espontáneo
            </a>
            <a 
              href="https://wa.me/595991344956?text=Hola,%20me%20gustaría%20trabajar%20en%20Zenex"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
            >
              <Phone className="w-5 h-5" />
              Contactar por WhatsApp
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default TrabajaPage;
